import { useMemo, useState, useTransition } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEventSlug } from '@/hooks/use-event-slug';
import { csrfToken } from '@/lib/csrf';
import type {
  StateProject,
  StateResponse,
  TradeResponse,
} from '@/types/market';
import { formatPercent } from './format';
import {
  applyBuy,
  applySell,
  bananasForSell,
  priceOf,
  sharesForBananas,
} from './lmsr';

export function TradePanel({
  side,
  project,
  state,
  myShares,
  onTraded,
}: {
  side: 'buy' | 'sell';
  project: StateProject;
  state: StateResponse;
  myShares: number;
  onTraded: () => void;
}) {
  const eventSlug = useEventSlug();
  const initialAmount = side === 'buy' ? state.market.minTradeBananas : 1;
  const [raw, setRaw] = useState<string>(String(initialAmount));
  const amount = Math.max(0, Math.floor(Number(raw) || 0));
  const [error, setError] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();

  const ids = useMemo(() => state.projects.map((p) => p.id), [state.projects]);
  const balance = state.myBalance ?? 0;

  const canTrade =
    (state.canTrade ?? state.myUserType === 'hacker') &&
    state.market.status === 'open';
  const sellDisabled = side === 'sell' && myShares <= 0;

  const preview = useMemo(() => {
    const q = state.qVector;
    const b = state.market.liquidityB;
    const fee = (gross: number) =>
      gross > 0
        ? Math.max(1, Math.ceil((gross * state.market.feeBps) / 10_000))
        : 0;

    if (side === 'buy') {
      const gross = Math.max(0, Math.min(amount, balance));
      if (gross < state.market.minTradeBananas) return null;
      const feeBananas = fee(gross);
      const net = gross - feeBananas;
      const { shares, actualCost } = sharesForBananas(
        q,
        b,
        ids,
        project.id,
        net,
      );
      if (shares <= 0) return null;
      const qNew = applyBuy(q, project.id, shares);
      const priceAfter = priceOf(qNew, b, ids, project.id);
      return {
        shares,
        gross: actualCost + feeBananas,
        fee: feeBananas,
        priceAfter,
        avgPrice: shares > 0 ? actualCost / shares : 0,
      };
    }
    const sharesToSell = Math.max(0, Math.min(amount, myShares));
    if (sharesToSell <= 0) return null;
    const grossProceeds = bananasForSell(q, b, ids, project.id, sharesToSell);
    const isFullClose = sharesToSell === myShares;
    if (grossProceeds <= 0) return null;
    if (!isFullClose && grossProceeds < state.market.minTradeBananas)
      return null;
    const feeBananas = fee(grossProceeds);
    const net = grossProceeds - feeBananas;
    const qNew = applySell(q, project.id, sharesToSell);
    const priceAfter = priceOf(qNew, b, ids, project.id);
    return {
      shares: sharesToSell,
      gross: net,
      fee: feeBananas,
      priceAfter,
      avgPrice: sharesToSell > 0 ? grossProceeds / sharesToSell : 0,
    };
  }, [side, amount, state, ids, project.id, balance, myShares]);

  const submit = () => {
    if (!preview) return;
    setError(null);
    startTransition(async () => {
      const res = await fetch(`/api/market/trade?event=${eventSlug}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          projectId: project.id,
          side,
          ...(side === 'buy' ? { bananas: amount } : { shares: amount }),
          expectedPriceAfter: preview.priceAfter,
          slippageBps: 200,
        }),
      });
      const data = (await res.json()) as TradeResponse;
      if ('ok' in data && data.ok) {
        setRaw(String(initialAmount));
        onTraded();
        return;
      }
      if ('error' in data) {
        if (data.error === 'PRICE_MOVED') {
          setError(
            `Cambió a ${formatPercent((data as { currentPriceAfter: number }).currentPriceAfter)}. Intenta de nuevo.`,
          );
          onTraded();
        } else if (data.error === 'INSUFFICIENT_FUNDS') {
          setError('Te faltan bananas.');
        } else if (data.error === 'INSUFFICIENT_SHARES') {
          const held = (data as { held?: number }).held;
          setError(
            held !== undefined
              ? `Solo tienes ${held} acciones (intentaste vender ${amount}).`
              : 'No tienes suficientes acciones.',
          );
          onTraded();
        } else if (data.error === 'TRADE_TOO_SMALL') {
          setError(`Mínimo ${state.market.minTradeBananas} 🍌.`);
        } else if (data.error === 'MARKET_NOT_OPEN') {
          setError('El mercado no está abierto.');
        } else if (data.error === 'HACKERS_ONLY') {
          setError('Solo los hackers pueden operar.');
        } else {
          setError(data.error);
        }
      }
    });
  };

  const inputLabel =
    side === 'buy' ? 'Bananas a invertir' : 'Acciones a vender';
  const max = side === 'buy' ? balance : myShares;
  const min = side === 'buy' ? state.market.minTradeBananas : 1;
  const disabled = !canTrade || sellDisabled;
  const isBuy = side === 'buy';
  const overMax = amount > max;
  const overMaxMessage = overMax
    ? isBuy
      ? `Solo tienes ${balance.toLocaleString()} 🍌`
      : `Solo tienes ${myShares} acciones`
    : null;

  return (
    <section className="flex flex-col gap-4 border-2 border-primary bg-background/80 p-6 backdrop-blur-sm">
      <div className="flex items-center justify-between border-primary/20 border-b pb-3">
        <h3 className="font-bold text-2xl text-primary">
          {isBuy ? 'Comprar' : 'Vender'}
        </h3>
        <span className="font-mono text-primary/70 text-xs uppercase tracking-wider">
          {isBuy ? `${balance.toLocaleString()} 🍌` : `${myShares} acciones`}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <label
          className="font-mono text-primary/70 text-xs uppercase tracking-wider"
          htmlFor={`amt-${side}`}
        >
          {inputLabel}
        </label>
        <Input
          id={`amt-${side}`}
          className={`rounded-none border-2 font-mono text-lg ${
            overMax
              ? 'border-destructive bg-destructive/10 text-destructive'
              : 'border-primary'
          }`}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={raw}
          disabled={disabled}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setRaw(e.target.value.replace(/[^0-9]/g, ''))}
        />
        {overMaxMessage && (
          <p className="font-mono text-destructive text-xs uppercase tracking-wider">
            {overMaxMessage}
          </p>
        )}
      </div>

      <div className="border-2 border-primary bg-background p-3">
        {preview ? (
          <div className="flex flex-col gap-1.5 font-mono text-sm">
            <Row label={isBuy ? 'Recibes' : 'Vendes'}>
              {preview.shares} acciones
            </Row>
            <Row label={isBuy ? 'Pagas (con fee)' : 'Recibes (neto)'}>
              {preview.gross} 🍌
            </Row>
            <Row label="Fee">{preview.fee} 🍌</Row>
            <Row label="Probabilidad después">
              {formatPercent(preview.priceAfter)}
            </Row>
          </div>
        ) : (
          <p className="font-mono text-primary/60 text-xs uppercase tracking-wider">
            {sellDisabled
              ? 'No tienes acciones'
              : isBuy
                ? `Mínimo ${state.market.minTradeBananas} 🍌`
                : `Vende todas tus ${myShares} acciones, o más para superar ${state.market.minTradeBananas} 🍌`}
          </p>
        )}
      </div>

      {error && (
        <p className="border-2 border-destructive bg-destructive/10 p-2 font-mono text-destructive text-xs">
          {error}
        </p>
      )}

      <Button
        className="rounded-none bg-primary py-6 font-bold text-background text-lg uppercase tracking-wider hover:bg-primary/90"
        onClick={submit}
        disabled={disabled || pending || !preview || overMax}
      >
        {pending
          ? 'Procesando…'
          : isBuy
            ? `Comprar ${preview?.shares ?? ''} acciones`
            : `Vender ${preview?.shares ?? ''} acciones`}
      </Button>
    </section>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-primary/60 text-xs uppercase tracking-wider">
        {label}
      </span>
      <span className="font-bold text-primary">{children}</span>
    </div>
  );
}
